// ============================================================================
// ExternalSignPage — standalone magic-link signing (Build S3, Task #19).
// ============================================================================
// Route: #sign/:token — no portal chrome, focused flow. The token was carried
// in the emailed link (the ONLY path-carried use); every subsequent call
// sends it in the request BODY (pin 16.2.8). All token failures render the
// same message — the API's uniform 401.
// ============================================================================

import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2, PenLine } from 'lucide-react';
import { api, type ExternalSignDetail, type SignInput } from '../api';
import { SignatureCapture } from '../components/SignatureCapture';

interface Props {
  linkToken: string;
}

export function ExternalSignPage({ linkToken }: Props) {
  const [detail, setDetail] = useState<ExternalSignDetail | null>(null);
  const [phase, setPhase] = useState<'loading' | 'review' | 'consent' | 'sign' | 'done' | 'declined' | 'invalid'>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.externalSignDetail(linkToken);
        if (cancelled) return;
        setDetail(d);
        setPhase(d.my_party.status === 'signed' ? 'done' : 'review');
      } catch {
        if (!cancelled) setPhase('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [linkToken]);

  async function consent() {
    setBusy(true); setError('');
    try {
      await api.externalSignConsent(linkToken);
      const d = await api.externalSignDetail(linkToken);
      setDetail(d); setPhase('sign');
    } catch (e: any) { setError(e?.message || 'Could not record consent'); }
    finally { setBusy(false); }
  }

  async function sign(input: SignInput) {
    setBusy(true); setError('');
    try {
      await api.externalSignSign(linkToken, input);
      setPhase('done');
    } catch (e: any) { setError(e?.message || 'Could not record signature'); }
    finally { setBusy(false); }
  }

  async function decline() {
    if (declineReason.trim().length < 2) return;
    setBusy(true); setError('');
    try {
      await api.externalSignDecline(linkToken, declineReason.trim());
      setPhase('declined');
    } catch (e: any) { setError(e?.message || 'Could not record decline'); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <PenLine className="h-5 w-5 text-uif-green" />
          <span className="font-semibold text-uif-green-dark">Up in Farms — Electronic Signing</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {phase === 'loading' && (
          <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-uif-green" /></div>
        )}

        {phase === 'invalid' && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center space-y-2">
            <AlertCircle className="h-8 w-8 text-amber-500 mx-auto" />
            <div className="font-semibold text-gray-900">This signing link is not valid</div>
            <p className="text-sm text-gray-500">
              The link may have expired or already been used. Contact support@upinfarms.com for a new one.
            </p>
          </div>
        )}

        {phase === 'done' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center space-y-2">
            <CheckCircle2 className="h-10 w-10 text-uif-green mx-auto" />
            <div className="font-semibold text-uif-green-dark">Your signature is recorded — thank you.</div>
            <p className="text-sm text-gray-600">
              When all parties have signed, you will receive the executed copy by email.
              This link is now closed.
            </p>
          </div>
        )}

        {phase === 'declined' && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center space-y-2">
            <div className="font-semibold text-gray-900">You declined to sign.</div>
            <p className="text-sm text-gray-500">Up in Farms has been notified. This link is now closed.</p>
          </div>
        )}

        {detail && (phase === 'review' || phase === 'consent' || phase === 'sign') && (
          <>
            <div>
              <h1 className="text-xl font-bold text-uif-green-dark">{detail.envelope.title}</h1>
              <p className="text-sm text-gray-500">
                For: {detail.my_party.display_name}
                {detail.my_party.title_role ? ` — ${detail.my_party.title_role}` : ''}
              </p>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
            )}
            {detail.envelope.signing_instructions && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900">
                {detail.envelope.signing_instructions}
              </div>
            )}
            {detail.preview_url && (
              <iframe title="Document preview" src={detail.preview_url}
                className="w-full h-[480px] border border-gray-200 rounded-lg bg-white" />
            )}

            {phase === 'review' && (
              <div className="flex gap-2">
                <button type="button" onClick={() => setPhase('consent')}
                  className="flex-1 rounded-md bg-uif-green text-white font-semibold px-4 py-2.5 hover:bg-uif-green-dark">
                  Continue to consent
                </button>
                <button type="button" onClick={() => setDeclineOpen(true)}
                  className="rounded-md border border-gray-300 text-gray-700 px-4 py-2.5 hover:border-red-400 hover:text-red-600">
                  Decline
                </button>
              </div>
            )}

            {phase === 'consent' && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                  {detail.consent_language}
                </pre>
                <div className="flex gap-2">
                  <button type="button" disabled={busy} onClick={() => void consent()}
                    className="flex-1 rounded-md bg-uif-green text-white font-semibold px-4 py-2.5 disabled:opacity-40 hover:bg-uif-green-dark">
                    {busy ? 'Recording…' : 'I agree and consent'}
                  </button>
                  <button type="button"
                    onClick={() => {
                      // §0.6 item-6: audit trace first, then mail (best-effort).
                      void api.externalSignPaperPreference(linkToken).catch(() => undefined);
                      window.location.href = 'mailto:support@upinfarms.com?subject=Prefer%20paper%20signing';
                    }}
                    className="rounded-md border border-gray-300 text-gray-700 px-4 py-2.5 hover:border-uif-green">
                    Prefer paper — contact us
                  </button>
                </div>
              </div>
            )}

            {phase === 'sign' && detail.my_party.consent_recorded && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <SignatureCapture
                  displayName={detail.my_party.display_name}
                  busy={busy}
                  onSubmit={(input) => void sign(input)}
                />
              </div>
            )}

            {declineOpen && (
              <div className="bg-white border border-red-200 rounded-lg p-4 space-y-2">
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Reason for declining *</span>
                  <textarea rows={2} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </label>
                <div className="flex gap-2">
                  <button type="button" disabled={busy || declineReason.trim().length < 2}
                    onClick={() => void decline()}
                    className="rounded-md bg-red-600 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
                    Decline to sign
                  </button>
                  <button type="button" onClick={() => setDeclineOpen(false)}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-4 py-6 text-center text-xs text-gray-400">
        © Up in Farms Food Hub · support@upinfarms.com
      </footer>
    </div>
  );
}
